import { useApiCall } from './useApiCall';
import { useCallback } from 'react';
import { IAccordionElement } from '../interfaces/quPage/quPage';
import { useCaseStore } from '../store/hooks';
import { IResponse } from '../interfaces/api/common';
import {
  IAddSimpleQuestion,
  IAddTypeQuestion
} from '../components/shared/modal/common/modalAddQuestion/ModalAddQuestion.types';
import {
  IDraftCaseAcromegaly,
  IDraftCaseCushing,
  IDraftCaseCushingV2
} from '../components/pages/user/caseDraft/CaseDraft.types';
import {
  convertProgressBarAcromegaly,
  convertProgressBarCushing,
  convertProgressBarCushingV2
} from '../components/pages/user/caseDraft/CaseDraft.helper';

export const useCaseApi = (handleClose?: () => void) => {
  const { api } = useApiCall();
  const caseStore = useCaseStore();

  const getForum = useCallback(
    async (idCase: number, type: string, loader: boolean, version?: string) => {
      const url = version ? `medical-qa/${type}/${version}/${idCase}` : `medical-qa/${type}/${idCase}`;
      const res: IAccordionElement[] = await api.apiInstance.get(url, loader);
      if (res) {
        caseStore.setForum(res);
      }
    },
    [caseStore, api.apiInstance]
  );

  const postForumElement = useCallback(
    async (idCase: number, type: string, question: IAddTypeQuestion, version?: string) => {
      const url = version
        ? `case/medical-question/${type}/${version}/${idCase}`
        : `case/medical-question/${type}/${idCase}`;
      const res: IResponse<IAccordionElement> = await api.apiInstance.post(url, question);
      await getForum(idCase, type, false, version);
      return res.code === 200;
    },
    [api.apiInstance, getForum]
  );

  const postForumAnswer = useCallback(
    async (idCase: number, type: string, questionName: number, answerName: string, version?: string) => {
      const res: IResponse<IAccordionElement> = await api.apiInstance.post('case/medical-answer', {
        questionName,
        answerName
      });
      await getForum(idCase, type, false, version);

      return res.code === 200;
    },
    [api.apiInstance, getForum]
  );

  const putForumAnswerVisibility = useCallback(
    async (idCase: number, idQuestion: number, type: string, visibility: boolean, version?: string) => {
      const res: IResponse<IAccordionElement> = await api.apiInstance.put(`medical-qa/visibility/${idQuestion}`, {
        visibility
      });

      await getForum(idCase, type, false, version);

      return res.code === 200;
    },
    [api.apiInstance, getForum]
  );

  const getDiscussion = useCallback(
    async (id: number, type: string, loader: boolean, version?: string) => {
      const url = version ? `discussion/all/${type}/${version}/${id}` : `discussion/all/${type}/${id}`;
      const res: IAccordionElement[] = await api.apiInstance.get(url, loader);

      if (res) {
        caseStore.setDiscussion(res ?? []);
      }
    },
    [caseStore, api.apiInstance]
  );

  const postDiscussionElement = useCallback(
    async (id: number, type: string, question: IAddSimpleQuestion, forWho: 'AUTHOR' | 'EXPERT', version?: string) => {
      const url = version ? `discussion-author/${type}/${version}/${id}` : `discussion-author/${type}/${id}`;
      const res: IResponse<IAccordionElement> = await api.apiInstance.post(url, {
        ...question,
        forWho
      });

      await getDiscussion(id, type, false, version);

      return res.code === 200;
    },
    [api.apiInstance, getDiscussion]
  );

  const postDiscussionAnswer = useCallback(
    async (idCase: number, type: string, questionId: number, answer: string, version?: string) => {
      const res: IResponse<IAccordionElement> = await api.apiInstance.post('discussion-expert', { questionId, answer });

      await getDiscussion(idCase, type, false, version);

      return res.code === 200;
    },
    [api.apiInstance, getDiscussion]
  );

  const putDiscussionAnswerVisibility = useCallback(
    async (idCase: number, id: number, type: string) => {
      const res: IResponse<IAccordionElement> = await api.apiInstance.put(`discussion/visibility/${id}`, {});

      await getDiscussion(idCase, type, false);
      if (res.code === 200) return res.code === 200;
    },
    [api.apiInstance, getDiscussion]
  );

  const getCase = useCallback(
    async (type: string, id: number, version?: string) => {
      const url = version ? `medical-case/${type}/${version}/${id}` : `medical-case/${type}/${id}`;
      const res = await api.apiInstance.get(url, true);

      if (res) {
        if (type === 'acromegaly') {
          const data: IDraftCaseAcromegaly = res.data;
          const convert = convertProgressBarAcromegaly(data);
          caseStore.addStages(
            convert.step,
            convert.status,
            convert.zeroStage,
            convert.firstStage,
            convert.secondStage,
            convert.thirdStage,
            convert.fourthStage
          );
        } else if (type === 'cushing') {
          if (version === 'v2') {
            const data: IDraftCaseCushingV2 = res.data;
            const convert = convertProgressBarCushingV2(data);

            caseStore.addStages(
              convert.step,
              convert.status,
              convert.zeroStage,
              convert.firstStage,
              convert.secondStage,
              convert.thirdStage,
              convert.fourthStage
            );
          } else {
            const data: IDraftCaseCushing = res.data;
            const convert = convertProgressBarCushing(data);

            caseStore.addStages(
              convert.step,
              convert.status,
              convert.zeroStage,
              convert.firstStage,
              convert.secondStage,
              convert.thirdStage,
              convert.fourthStage
            );
          }
        }
        return true;
      }
      return false;
    },
    [api.apiInstance, caseStore]
  );

  const createComment = useCallback(
    async (type: string, id: number, comment: string, version?: string) => {
      const url = version ? `medical-case/${type}/${version}/notes/${id}` : `medical-case/${type}/notes/${id}`;
      const res = await api.apiInstance.post(url, { comment }, true);
      if (res) {
        return !!res;
      }
    },
    [api.apiInstance]
  );

  const getComments = useCallback(
    async (type: string, id: number, isLoading: boolean, version?: string) => {
      const url = version ? `medical-case/${type}/${version}/notes/${id}` : `medical-case/${type}/notes/${id}`;
      const res = await api.apiInstance.get(url, isLoading);

      if (res) {
        caseStore.setComments(res);
      }
    },
    [api.apiInstance, caseStore]
  );

  const postReportExcel = useCallback(async () => {
    const res = await api.apiInstance.get(`medical-cases/excel`, true, { responseType: 'blob' });

    if (res) {
      const blob = new Blob([res], { type: 'application/xlsx' });

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'Report_' + new Date().toLocaleDateString() + '.xlsx';
      link.click();
    }
  }, [api.apiInstance]);

  const downloadCaseViewPdf = useCallback(
    async (id: string, type: string, version?: string) => {
      const url = version ? `medical-case/${type}/${version}/pdf/${id}` : `medical-case/${type}/pdf/${id}`;
      const res = await api.apiInstance.get(url, true, { responseType: 'blob' });

      if (res) {
        const blob = new Blob([res], { type: 'application/pdf' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'download.pdf');
        document.body.appendChild(link);
        link.click();
      }
    },
    [api.apiInstance]
  );

  return {
    getForum,
    postForumElement,
    postForumAnswer,
    putForumAnswerVisibility,
    postReportExcel,
    getDiscussion,
    postDiscussionElement,
    postDiscussionAnswer,
    putDiscussionAnswerVisibility,
    getCase,
    createComment,
    getComments,
    downloadCaseViewPdf
  };
};
